/* eslint-disable no-script-url */
/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from "react";
import parse from "html-react-parser";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import Loader from "../Components/Common/Loader";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  deleteFromWishlist,
  getProductById,
  getProductRecomendation,
  postAddToWishlist,
} from "../Utils/api-service";
import { extractNumberFromString, vibrate } from "../Utils/util-service";
import {
  findCartItem,
  getCart,
  getProductCartSummary,
  normalizeProductId,
  setCart,
  upsertCartItem,
} from "../Utils/cart-service";
import AppContext from "../Utils/appContext";
import { possibleNames } from "../Utils/constant";
import SimilarProduct from "../Components/SimilarProduct";
import HeaderOptions from "../Components/Common/HeaderOptions";
import ProductBadges from "../Components/ProductBadges";
import ProductImageSlider from "../Components/ProductImageSlider";
import {
  addToWishlist,
  getWishlist,
  removeFromWishlist,
} from "../Utils/wishlist-service";
import {
  buildWishlistUiStateMap,
  clearWishlistAnimateInMap,
  getWishlistClassName,
  toggleWishlistInMap,
} from "../Utils/wishlist-ui";
import { showToast } from "../Utils/toast-service";

export default function ProductDetail() {
  const { setCartLength, bumpCartVersion, cartVersion, showPrice } =
    React.useContext(AppContext);

  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [disabled, setDisabled] = React.useState(false);
  const [showErr, setShowErr] = React.useState(false);
  const [price, setPrice] = React.useState();
  const [product, setProduct] = React.useState({});
  // selectedOption will be like [{index: 0, value: 1.5D}]
  const [selectedOption, setSelectedOption] = React.useState([]);
  const [activeIndices, setActiveIndices] = React.useState([]);
  let [searchParams] = useSearchParams();
  const productId = searchParams.get("productId");
  const isZoomOpen = searchParams.get("zoom") === "1";

  const [quantity, setQuantity] = React.useState(1);
  const [totalPc, setTotalPc] = React.useState();
  const [unit, setUnit] = React.useState();
  // Cart config
  const [cart, setCartState] = React.useState(getCart());
  const [cartMatchQty, setCartMatchQty] = React.useState(null);
  const [otherPackInCart, setOtherPackInCart] = React.useState(false);
  const [productRecommendations, setProductRecommendations] = React.useState(
    [],
  );
  const [selectedImage, setSelectedImage] = React.useState();
  // Mount TransformWrapper only after the modal is visible (needs real dimensions)
  const [zoomReady, setZoomReady] = React.useState(false);
  const wasZoomOpen = React.useRef(false);
  const [wishlistState, setWishlistState] = React.useState(() =>
    buildWishlistUiStateMap(getWishlist()),
  );
  React.useEffect(() => {
    setCart(cart);
  }, [cart]);

  // Prefill qty from cart for the currently selected variant (after default inventory selection).
  React.useEffect(() => {
    if (!productId || !selectedOption?.length || !product?.id) {
      setCartMatchQty(null);
      setOtherPackInCart(false);
      return;
    }
    if (normalizeProductId(product.id) !== normalizeProductId(productId)) {
      return;
    }
    const latestCart = getCart();
    const match = findCartItem(latestCart, productId, selectedOption);
    const summary = getProductCartSummary(latestCart, productId);
    if (match) {
      setCartMatchQty(Number(match.quantity) || 1);
      setQuantity(Number(match.quantity) || 1);
      setOtherPackInCart(false);
    } else {
      setCartMatchQty(null);
      setQuantity(1);
      setOtherPackInCart(summary.inCart);
    }
  }, [productId, product?.id, selectedOption, cartVersion]);

  const toggleWishlist = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!product?.id) return;

    const isActive = Boolean(wishlistState[product.id]?.active);
    setWishlistState((prev) => toggleWishlistInMap(prev, product.id));

    if (isActive) {
      removeFromWishlist(product.id);
      const response = await deleteFromWishlist({
        shopifyProductId: product.id,
      });
      if (response?.status !== 200 || response?.data?.removed === false) {
        setWishlistState((prev) => ({
          ...prev,
          [product.id]: buildWishlistUiStateMap([product.id])[product.id],
        }));
        addToWishlist(product.id);
      }
      return;
    }

    addToWishlist(product.id);
    const response = await postAddToWishlist({
      shopifyProductId: product.id,
      source: "PDP",
    });

    if (response?.status !== 201 && response?.status !== 200) {
      setWishlistState((prev) => toggleWishlistInMap(prev, product.id));
      removeFromWishlist(product.id);
      return;
    }

    showToast("Added to wishlist");

    setTimeout(() => {
      setWishlistState((prev) => clearWishlistAnimateInMap(prev, product.id));
    }, 700);
  };

  const getImageModal = () => {
    const el = document.getElementById("productImageZoomModal");
    if (!el || !window.bootstrap?.Modal) return null;
    return window.bootstrap.Modal.getOrCreateInstance(el);
  };

  const openImageZoom = (url) => {
    if (!url) return;
    setSelectedImage(url);
    if (!isZoomOpen) {
      const params = new URLSearchParams(searchParams);
      params.set("zoom", "1");
      navigate(`?${params.toString()}`);
    }
  };

  const closeImageZoom = () => {
    if (isZoomOpen) {
      navigate(-1);
      return;
    }
    setZoomReady(false);
    setSelectedImage();
    getImageModal()?.hide();
  };

  // Drop stale zoom param on fresh load/refresh (no in-memory image)
  React.useEffect(() => {
    if (searchParams.get("zoom") !== "1") return;
    const params = new URLSearchParams(searchParams);
    params.delete("zoom");
    navigate(`?${params.toString()}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep Bootstrap modal in sync with the zoom URL param (covers native back)
  React.useEffect(() => {
    const modal = getImageModal();
    if (!modal) return;

    if (isZoomOpen && selectedImage) {
      modal.show();
    } else if (wasZoomOpen.current && !isZoomOpen) {
      setZoomReady(false);
      modal.hide();
      setSelectedImage();
    }

    wasZoomOpen.current = isZoomOpen;
  }, [isZoomOpen, selectedImage]);

  // Enable zoom only after modal is fully shown; pop URL if closed via backdrop/ESC
  React.useEffect(() => {
    const el = document.getElementById("productImageZoomModal");
    if (!el) return;

    const onShown = () => setZoomReady(true);
    const onHidden = () => {
      setZoomReady(false);
      if (searchParams.get("zoom") === "1") {
        navigate(-1);
      }
    };
    el.addEventListener("shown.bs.modal", onShown);
    el.addEventListener("hidden.bs.modal", onHidden);
    return () => {
      el.removeEventListener("shown.bs.modal", onShown);
      el.removeEventListener("hidden.bs.modal", onHidden);
    };
  }, [searchParams, navigate]);

  //
  let packSizePosition;

  React.useEffect(() => {
    if (productId) {
      fetchProduct(productId);
      fetchProductRecomendation(productId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);
  const fetchProduct = (id) => {
    setLoading(true);
    try {
      let res = getProductById(id);
      res.then((data) => {
        if (data.status === 200) {
          const ps = data?.data?.product || [];
          setProduct(ps);
          setLoading(false);
          setQuantity(1);
          setDisabled(false);
        }
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchProductRecomendation = (id) => {
    try {
      let res = getProductRecomendation(productId);
      res
        .then((data) => {
          if (data.status === 200) {
            setProductRecommendations(data?.data?.productRecommendations || []);
          }
        })
        .catch((err) => {
          setProductRecommendations([]);
        });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const splitTitleToArray = (title) =>
    title.split(" / ").map((str) => str.trim());

  React.useEffect(() => {
    if (product?.options) {
      setActiveIndices(
        product?.options?.length
          ? Array(product?.options?.length).fill(null)
          : null,
      );
      if (product?.totalInventory === 0) {
        setDisabled(true);
      }
      if (product?.variants?.nodes.length === 1) {
        setPrice(product?.priceRange?.minVariantPrice?.amount);
        setTotalPc(1);
        setUnit("PC");
      }
      product?.options?.forEach((option, indx) => {
        if (possibleNames.includes(option?.name.toLowerCase())) {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          packSizePosition = indx;
        }
      });
    }
    let found = false;
    // Check if all variant has all zero  quantityAvailable
    let weHaveNothingToSelect;
    let variantLengths = 0;
    //  // Check if all variant has all zero  quantityAvailable ends
    if (product?.variants && product?.variants?.nodes) {
      product?.variants?.nodes?.forEach((variant, indx) => {
        const title = variant?.title;
        const arr = splitTitleToArray(title);
        if (variant?.quantityAvailable > 0) {
          if (!found) {
            // onload option selected
            setSelectedOption(arr);
            const extractData = extractNumberFromString(arr[packSizePosition]);
            setTotalPc(extractData?.number || 1);
            setUnit(extractData?.unit || "PC");
            setPrice(variant?.price?.amount);
          }
          variant.compareQty = arr;
          found = true;
        } else {
          variant.compareQty = arr;
          if (indx === 0) {
            weHaveNothingToSelect = arr;
          }
          variantLengths += 1;
        }
      });
      if (variantLengths === product?.variants?.nodes?.length) {
        setSelectedOption(weHaveNothingToSelect);
        const extractData = extractNumberFromString(weHaveNothingToSelect[0]);
        setTotalPc(extractData?.number || 1);
        setUnit(extractData?.unit || "PC");
        setPrice(product?.variants?.nodes[0]?.price?.amount);
      }
    }
  }, [product?.options]);

  // compare array
  function commonElements(arr1, arr2) {
    if (arr1?.length > 0 && arr1?.length > 0) {
      return arr1.every((element) => arr2.includes(element));
    }
    return false;
  }

  const selectOption = (arrayIndex, itemIndex, optionVal) => {
    // Below code help in selecting options and setting their class to active
    const newActiveIndices = [...activeIndices];
    newActiveIndices[arrayIndex] = itemIndex;
    setActiveIndices(newActiveIndices);
    // Below code ends

    // Set selected option
    const optionsArr = [...selectedOption];
    optionsArr[arrayIndex] = optionVal;
    setSelectedOption(optionsArr);
    const extractData = extractNumberFromString(optionsArr[packSizePosition]);
    setTotalPc(extractData?.number || 0);
    setUnit(extractData?.unit || "PC");

    let returnVal;
    let found = false;
    if (product?.variants && product?.variants?.nodes) {
      product?.variants.nodes.forEach((variant) => {
        if (found) return; // Skip iteration if already found
        if (commonElements(variant.compareQty, optionsArr)) {
          setPrice(variant?.price?.amount);
          returnVal = variant?.quantityAvailable;
        }
      });
    }
    setShowErr(returnVal < 1);
    setDisabled(returnVal < 1);
  };

  const fetchVariantBadgePrice = React.useCallback(
    (arrayIndex, optionVal) => {
      let priceReturn = 0;

      // clone selected options and replace with new selection
      const optionsArr = [...selectedOption];
      optionsArr[arrayIndex] = optionVal;

      // extract number from pack size
      const extractData = extractNumberFromString(optionsArr[packSizePosition]);
      const totalPCValue = extractData?.number || 1; // fallback to 1 to avoid NaN

      if (product?.variants?.nodes) {
        for (const variant of product.variants.nodes) {
          if (commonElements(variant.compareQty, optionsArr)) {
            priceReturn = Number(variant?.price?.amount) || 0;
            break; // stop after first match
          }
        }
      }

      return (priceReturn / totalPCValue).toFixed(2);
    },
    [product, selectedOption, packSizePosition], // dependencies
  );

  // const fetchVariantBadgePrice = (arrayIndex, itemIndex, optionVal) => {
  //   // console.log("sfdsf", product?.variants.nodes, optionVal, selectedOption);
  //   let priceReturn;
  //   // Below code help in selecting options and setting their class to active
  //   const newActiveIndices = [...activeIndices];
  //   newActiveIndices[arrayIndex] = itemIndex;
  //   // Set selected option
  //   const optionsArr = [...selectedOption];
  //   optionsArr[arrayIndex] = optionVal;
  //   const extractData = extractNumberFromString(optionsArr[packSizePosition]);
  //   const totalPCValue = extractData?.number || 0;
  //   let found = false;
  //   if (product?.variants && product?.variants?.nodes) {
  //     product?.variants.nodes.forEach((variant) => {
  //       if (found) return; // Skip iteration if already found
  //       if (commonElements(variant.compareQty, optionsArr)) {
  //         // debugger;
  //         priceReturn = variant?.price?.amount;
  //       }
  //     });
  //   }
  //   priceReturn = (priceReturn / totalPCValue).toFixed(2);
  //   return priceReturn;
  // };

  const comparePackSize = (arrayIndex, name, val) => {
    let returnVal = true;

    if (possibleNames.includes(name.toLowerCase())) {
      packSizePosition = arrayIndex;
      let options = [...selectedOption];
      options = options.filter((_, i) => i !== arrayIndex);
      options[arrayIndex] = val;
      if (product?.variants && product?.variants?.nodes) {
        product?.variants.nodes.forEach((variant) => {
          if (commonElements(variant.compareQty, options)) {
            returnVal = variant?.quantityAvailable > 0;
          }
        });
      }
      return returnVal;
    } else {
      return returnVal;
    }
  };

  const handleIncrement = () => {
    vibrate(20);
    setQuantity((prevQuantity) => prevQuantity + 1);
  };

  const handleDecrement = () => {
    vibrate(20);
    setQuantity((prevQuantity) => (prevQuantity > 1 ? prevQuantity - 1 : 1));
  };

  const handleAddToCart = () => {
    // Ensure price is a number to avoid type coercion issues
    const priceAsNumber = parseFloat(price) || 0;
    const selectedProduct = {
      id: productId,
      title: product.title,
      price: priceAsNumber,
      totalPrice: priceAsNumber * quantity,
      quantity,
      totalPc,
      unit,
      selectedOption,
      options: product?.options,
      vendor: product?.vendor,
      productType: product?.productType,
      tags: product?.tags || [],
      images: product?.images?.nodes || [],
      unitPrice: (priceAsNumber / totalPc).toFixed(2),
    };
    setCartState(() => upsertCartItem(getCart(), selectedProduct));
    vibrate(30);
    setTimeout(() => {
      const carts = getCart();
      setCartLength(carts.length);
      bumpCartVersion?.();
      navigate("/cart");
    }, 100);
  };

  const isUpdatingCart = cartMatchQty != null;
  return (
    <>
      {loading && <Loader />}
      <header>
        <div onClick={() => navigate(-1)} className="nav-bar">
          <i className="iconly-Arrow-Left icli"></i>
        </div>
        <a onClick={() => navigate("/")} className="brand-logo">
          <img src="assets/images/logo.png" className="img-fluid" alt="" />
        </a>
        <HeaderOptions />
      </header>
      <section className="product-page-section top-space pt-0">
        {/* <div className="rate-section px-15">
          <ul>
            <li>
              <i className="iconly-Home icli"></i> Home
            </li>
            <li>
              <i className="iconly-Arrow-Right-2 icli"></i>Categories
            </li>
            <li>
              <i className="iconly-Arrow-Right-2 icli"></i>Sub Category
            </li>
          </ul>
        </div> */}

        <div className="product-page-image-slider">
          <ProductImageSlider
            images={product?.images?.nodes || []}
            onImageClick={openImageZoom}
          />
          <ProductBadges
            tags={product?.tags}
            className="product-page-slider-badges"
          />
          {product?.id && (
            <div
              className={`${getWishlistClassName(
                wishlistState[product.id],
              )} product-page-wishlist-btn`}
              onClick={toggleWishlist}
              role="button"
              tabIndex={0}
              aria-label="Toggle wishlist"
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  toggleWishlist(event);
                }
              }}
            >
              <i className="iconly-Heart icli"></i>
              <i className="iconly-Heart icbo"></i>
              {wishlistState[product.id]?.animate && (
                <div className="effect-group">
                  <span className="effect"></span>
                  <span className="effect"></span>
                  <span className="effect"></span>
                  <span className="effect"></span>
                  <span className="effect"></span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="product-detail-box px-15 pt-2">
          <div className="main-detail">
            <h2 className="text-capitalize">{product?.title}</h2>
            <div className="price">
              {showPrice && (
                <div id="estimate-product-detail-pricing">
                  <span id="color-orange" className="content-color">
                    <span className="text-green">Total Qty.{"  "}</span>
                    <span id="color-orange">
                      {" "}
                      {totalPc * quantity || ""} {unit} {"  "}
                    </span>
                    {showPrice &&
                      price &&
                      totalPc &&
                      unit &&
                      `@ ₹${(price / totalPc).toFixed(2)} / ${unit}`}
                  </span>
                  <div>
                    <i
                      id="color-orange"
                      className="iconly-Arrow-Right icli"
                    ></i>
                  </div>
                  <strong className="text-green">₹{price * quantity}</strong>
                </div>
              )}
              {product?.totalInventory === 0 && (
                <h4>
                  <span>OUT OF STOCK</span>
                </h4>
              )}
            </div>
            {/* <h6 className="text-green">inclusive of all taxes</h6> */}
          </div>
        </div>
        <div className="divider"></div>
        <div className="product-detail-box px-15">
          {product?.options &&
            product?.options.length > 0 &&
            product?.options.map((option, arrayIndex) => {
              if (option?.name === "Title") {
                return null;
              }
              return (
                <div key={arrayIndex} className="filter-box">
                  <h2
                    id={
                      arrayIndex > 0 ? "product-option-spacingfrom-2" : "none"
                    }
                    className="filter-title"
                  >
                    {option?.name}:
                  </h2>
                  <div className="filter-content">
                    <ul className="row filter-row g-3">
                      {option?.values &&
                        option?.values.map((val, itemIndex) => {
                          return (
                            <li
                              key={itemIndex}
                              id={
                                comparePackSize(arrayIndex, option?.name, val)
                                  ? ""
                                  : "estimate-strike"
                              }
                              className={
                                selectedOption[arrayIndex] === val
                                  ? "col-4 active"
                                  : "col-4"
                              }
                              onClick={() =>
                                selectOption(arrayIndex, itemIndex, val)
                              }
                            >
                              <div
                                id="estimate-relative"
                                className="filter-col"
                              >
                                {val}
                                {showPrice &&
                                  product?.options.length - 1 ===
                                    arrayIndex && (
                                    <label
                                      className="badge"
                                      id={
                                        selectedOption[arrayIndex] === val
                                          ? "estimate-per-price-badge-active"
                                          : "estimate-per-price-badge"
                                      }
                                    >
                                      <span>
                                        @ ₹
                                        {fetchVariantBadgePrice(
                                          arrayIndex,
                                          val,
                                        )}{" "}
                                        / {unit}{" "}
                                      </span>
                                    </label>
                                  )}
                              </div>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                </div>
              );
            })}
          <div className="size-detail">
            <h2 id="product-option-spacingfrom-2" className="size-title">
              TOTAL QUANTITY:
            </h2>
            <div id="estimate-qty-product" className="qty-counter">
              <li id="estimate-selected-product" className="col-6">
                <h1 id="color-orange" className="content-color">
                  Qty : {totalPc * quantity || ""} {unit}
                </h1>
                {/* <span className="content-color">
                  {showPrice &&
                    price &&
                    totalPc &&
                    unit &&
                    `@ ₹${(price / totalPc).toFixed(2)} per ${unit}`}
                </span> */}
                {/* {showPrice && (
                  <h6 className="text-green">
                    Total Amount: <strong> ₹{price * quantity}</strong>
                  </h6>
                )} */}
                {/* <div className="filter-col">9000 Pcs (90/Pc)</div> */}
              </li>
              <div id="estimate-height-input" className="input-group">
                <button
                  type="button"
                  className="btn quantity-left-minus"
                  onClick={handleDecrement}
                >
                  <img
                    src="assets/svg/minus-square.svg"
                    className="img-fluid"
                    alt=""
                  />
                </button>
                <input
                  type="text"
                  name="quantity"
                  readOnly
                  className="form-control form-theme qty-input input-number"
                  value={quantity}
                />
                <button
                  type="button"
                  className="btn quantity-right-plus"
                  onClick={handleIncrement}
                >
                  <img
                    src="assets/svg/plus-square.svg"
                    className="img-fluid"
                    alt=""
                  />
                </button>
              </div>
            </div>
          </div>
          {showErr && (
            <section id="flash" className="alert-classic pt-0">
              <div
                className="alert alert-warning d-flex align-items-center"
                role="alert"
              >
                {/* Error Message Qty */}
                <i className="iconly-Danger icli"></i>
                <div>Please select different pack quantity.</div>
              </div>
            </section>
          )}
          {isUpdatingCart && (
            <section className="alert-classic pt-0">
              <div
                className="alert alert-success d-flex align-items-center"
                role="alert"
              >
                <i className="iconly-Bag icli"></i>
                <div>
                  Already in cart· Qty : {totalPc * cartMatchQty || ""} {unit}
                </div>
              </div>
            </section>
          )}
          {!isUpdatingCart && otherPackInCart && (
            <section className="alert-classic pt-0">
              <div
                className="alert alert-info d-flex align-items-center"
                role="alert"
              >
                <i className="iconly-Info-Circle icli"></i>
                <div>Other pack size already in cart</div>
              </div>
            </section>
          )}
          <div id="shakeX">
            <button
              disabled={disabled}
              id="estimate-100"
              type="button"
              className="btn btn-danger"
              onClick={() => handleAddToCart()}
            >
              {isUpdatingCart ? "Update Cart" : "Add To Cart"}
            </button>
          </div>
        </div>
        <div className="divider"></div>
        <div className="product-detail-box px-15">
          <h4 className="page-title mb-1">Product Details</h4>
          {product?.descriptionHtml && parse(product?.descriptionHtml)}
        </div>
        <div className="divider"></div>
      </section>
      {productRecommendations && productRecommendations.length > 0 && (
        <section className="pt-0 product-slider-section overflow-hidden">
          <div className="title-section px-15">
            <h2>Similar Products</h2>
          </div>
          <SimilarProduct recommendedProducts={productRecommendations} />
        </section>
      )}
      <section className="panel-space"></section>
      <div
        className="modal filter-modal fade"
        id="productImageZoomModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-fullscreen">
          <div className="modal-content">
            <div className="modal-header">
              <a
                href="javascript:void(0)"
                className="close-modal"
                onClick={closeImageZoom}
              >
                <img src="assets/svg/x-dark.svg" className="img-fluid" alt="" />
              </a>
            </div>
            <div
              className="modal-body"
              style={{ height: "100%", overflow: "hidden", padding: 0 }}
            >
              {selectedImage && zoomReady && (
                <TransformWrapper
                  key={selectedImage}
                  initialScale={1}
                  minScale={1}
                  maxScale={4}
                  centerOnInit
                >
                  <TransformComponent
                    wrapperStyle={{ width: "100%", height: "100%" }}
                    contentStyle={{ width: "100%" }}
                  >
                    <img
                      src={selectedImage}
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                      }}
                      className="bg-img"
                      alt=""
                    />
                  </TransformComponent>
                </TransformWrapper>
              )}
            </div>
            <div className="modal-footer">
              <a
                href="javascript:void(0)"
                className="reset-link"
                onClick={closeImageZoom}
              >
                CLOSE
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
